import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateUser } from "./utils";

import type { MutationConfig } from "@/lib/react-query";
import type { User } from "@/types/user";

type UpdateStockImagesVisibilityInput = {
  carId: string;
  hidden: boolean;
};

type UseUpdateStockImagesVisibilityOptions = {
  mutationConfig?: MutationConfig<(data: UpdateStockImagesVisibilityInput) => Promise<User>>;
};

const withStockImagesVisibility = (
  user: User,
  { carId, hidden }: UpdateStockImagesVisibilityInput
): User => {
  const hiddenCarIds = user.additional_info.stockImagesHiddenCarIds ?? [];
  const stockImagesHiddenCarIds = hidden
    ? [...new Set([...hiddenCarIds, carId])]
    : hiddenCarIds.filter((hiddenCarId) => hiddenCarId !== carId);

  return {
    ...user,
    additional_info: {
      ...user.additional_info,
      stockImagesHiddenCarIds,
    },
  };
};

export const useUpdateStockImagesVisibility = ({
  mutationConfig,
}: UseUpdateStockImagesVisibilityOptions = {}) => {
  const queryClient = useQueryClient();
  const {
    onSuccess,
    onMutate: _onMutate,
    onError: _onError,
    onSettled: _onSettled,
    ...restConfig
  } = mutationConfig || {};

  return useMutation({
    mutationFn: (variables: UpdateStockImagesVisibilityInput) => {
      const profile =
        queryClient.getQueryData<User>(["profile"]) ??
        queryClient.getQueryData<User>(["authenticated-user"]);

      if (!profile) throw new Error("User data not available");

      return updateUser(withStockImagesVisibility(profile, variables));
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["profile"] });
      await queryClient.cancelQueries({ queryKey: ["authenticated-user"] });

      const previousProfile = queryClient.getQueryData<User>(["profile"]);
      const previousAuthedUser = queryClient.getQueryData<User>(["authenticated-user"]);

      if (previousProfile) {
        queryClient.setQueryData(
          ["profile"],
          withStockImagesVisibility(previousProfile, variables)
        );
      }

      if (previousAuthedUser) {
        queryClient.setQueryData(
          ["authenticated-user"],
          withStockImagesVisibility(previousAuthedUser, variables)
        );
      }

      return { previousProfile, previousAuthedUser };
    },
    onError: (error, variables, context, mutation) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(["profile"], context.previousProfile);
      }
      if (context?.previousAuthedUser) {
        queryClient.setQueryData(["authenticated-user"], context.previousAuthedUser);
      }

      _onError?.(error, variables, context, mutation);
    },
    onSuccess: (data, variables, context, mutation) => {
      queryClient.setQueryData(["profile"], data);
      onSuccess?.(data, variables, context, mutation);
    },
    onSettled: (data, error, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      _onSettled?.(data, error, variables, context, mutation);
    },
    ...restConfig,
  });
};
